import ChatService from "../../../front-services/ChatService";
import ChatCache from "../../../utils/ChatCache";
import Analytics from "../../../utils/Analytics";
import HostApi from "../../../utils/HostApi";
import { useSliderState } from "../components/slider/SliderState";
import Utility from "./Utility";
import localization from "../../../assets/localization";

const chatCache = new ChatCache();

// Mutable per-turn context threaded through the contract-v2 stream handlers.
interface V2StreamContext {
  setMessages: any;
  setIsMessageLoading: any;
  setIsMessageCompleted: any;
  setThreadId: any;
  setToolName: any;
  assistant: any;
  threadId: any;
  isMobile: boolean;
  botResponse: { messageReply: string; metadata: any };
  messageId: string;
  done: boolean;
  /**
   * The host API's "reply" fires once per assistant turn. A stream that drops
   * after `message.completed` resumes from Last-Event-ID and should not replay
   * the frame, but a host page that pushes replies into its own analytics
   * cannot survive being told twice, so we do not depend on the server for it.
   */
  replyEmitted: boolean;
}
//const ERROR_MESSAGE =
//  "From fe: Siamo spiacenti, ma si sono verificati degli errori. Ti dispiacerebbe riprova più tardi? Ci scusiamo per il disagio. 😔";

export default class MessageHandler {
  chatService: ChatService;

  constructor() {
    this.chatService = new ChatService();
  }
  handleUserMessageWithoutStream = async (
    inputMessage: any,
    setMessages: any,
    setIsMessageLoading: any,
    _: any,
    setThreadId: any,
    assistant: any,
    threadId: any,
    _1: any,
    language: any,
    isMobile = false
  ) => {
    let botResponse = await this.sendMessageWithoutStream(
      inputMessage,
      setIsMessageLoading,
      assistant,
      threadId,
      setThreadId,
      language
    );

    if (botResponse) {
      let message = {
        type: "bot",
        content: botResponse.messageReply,
        metadata: botResponse.metadata,
        sensitive_alerts: botResponse.sensitive_alerts,
        time: Utility.getTime(),
      };

      setMessages((prevMessages: any) => [...prevMessages, message]);
      chatCache.addMessage(message, botResponse.threadId, assistant.id);
      Analytics.track("reply_received", { has_templates: false });
      HostApi.emit("reply", {
        text: botResponse.messageReply,
        threadId: botResponse.threadId,
        hasTemplates: false,
      });
    }
  };

  sendMessageWithoutStream = async (
    userMessage: any,
    setIsMessageLoading: any,
    assistant: any,
    threadId: any,
    setThreadId: any,
    language: any
  ) => {
    setIsMessageLoading(true);

    const response = await this.chatService.sendMessage(
      userMessage,
      assistant.id,
      threadId,
      language
    );

    if (response.ok) {
      const dataRaw = await response.json();

      this.rememberWidgetToken(assistant.id, dataRaw);

      const data: any = {
        messageReply: dataRaw.response,
        threadId: dataRaw.thread_id,
        widgetToken: this.readWidgetToken(dataRaw),
        assistantId: assistant.id,
        metadata: dataRaw.metadata,
        sensitive_alerts: dataRaw.sensitive_alerts || dataRaw.event?.sensitive_alerts || null, 
        fingerprint: chatCache.getFingerprint(),
      };

      setIsMessageLoading(false);
      setThreadId(data.threadId);
      return data;
    }
    setIsMessageLoading(false);

    return localization.get(language).error_generic;
  };

  handleUserMessageWithStream = async (
    inputMessage: any,
    setMessages: any,
    setIsMessageLoading: any,
    setIsMessageCompleted: any,
    setThreadId: any,
    assistant: any,
    threadId: any,
    setToolName: any,
    language: any,
    isMobile = false
  ) => {
    await this.sendMessageWithStream(
      inputMessage,
      assistant,
      threadId,
      setMessages,
      setIsMessageLoading,
      setIsMessageCompleted,
      setThreadId,
      setToolName,
      language,
      isMobile
    );
  };

  // Bounded resume attempts on a mid-stream network drop. The run is keyed
  // server-side by client_message_id, so resuming with Last-Event-ID replays
  // the buffered tail and never re-runs the turn.
  private static MAX_STREAM_RESUMES = 3;

  sendMessageWithStream = async (
    inputMessage: any,
    assistant: any,
    threadId: any,
    setMessages: any,
    setIsMessageLoading: any,
    setIsMessageCompleted: any,
    setThreadId: any,
    setToolName: any,
    language: any,
    isMobile = false
  ) => {
    const ctx: V2StreamContext = {
      setMessages,
      setIsMessageLoading,
      setIsMessageCompleted,
      setThreadId,
      setToolName,
      assistant,
      threadId,
      isMobile,
      botResponse: { messageReply: "", metadata: null },
      messageId: Utility.generateUniqueId(),
      done: false,
      replyEmitted: false,
    };

    const clientMessageId = Utility.generateUniqueId();
    let lastEventId: string | null = null;

    for (
      let attempt = 0;
      !ctx.done && attempt <= MessageHandler.MAX_STREAM_RESUMES;
      attempt++
    ) {
      let response: Response;
      try {
        response = await this.chatService.openV2Stream(
          {
            message: inputMessage,
            assistantId: assistant.id,
            threadId,
            language,
            fingerprint: chatCache.getFingerprint(),
            clientMessageId,
          },
          lastEventId
        );
      } catch (err) {
        // Could not open the connection — retry until the budget is spent.
        if (attempt >= MessageHandler.MAX_STREAM_RESUMES) {
          this.emitStreamError(ctx, err);
          break;
        }
        continue;
      }

      if (!response.ok || !response.body) {
        this.emitStreamError(ctx, response.statusText || "stream_unavailable");
        break;
      }

      try {
        lastEventId = await this.consumeV2Stream(response.body, ctx, lastEventId);
      } catch (err) {
        // Reader dropped mid-stream before a terminal frame — resume.
        if (ctx.done || attempt >= MessageHandler.MAX_STREAM_RESUMES) {
          if (!ctx.done) this.emitStreamError(ctx, err);
          break;
        }
        continue;
      }
      // If the stream closed without a terminal frame we loop and resume via
      // Last-Event-ID; ctx.done short-circuits the loop on a clean terminal.
    }

    return ctx.botResponse;
  };

  // Reads the SSE body, splitting on blank-line frame boundaries, tracking the
  // last `id:` for resume, and dispatching each JSON `data:` payload to the v2
  // event handler. Returns the last seen event id.
  private consumeV2Stream = async (
    body: ReadableStream<Uint8Array>,
    ctx: V2StreamContext,
    lastEventId: string | null
  ): Promise<string | null> => {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sepIndex: number;
        while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
          const rawFrame = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);

          const frame = this.parseSseFrame(rawFrame);
          if (frame.id) lastEventId = frame.id;
          if (!frame.data) continue;

          let payload: any;
          try {
            payload = JSON.parse(frame.data);
          } catch (error) {
            console.error("Failed to parse v2 stream frame:", error);
            continue;
          }

          this.handleV2Event(payload, ctx);

          if (ctx.done) {
            try {
              await reader.cancel();
            } catch {
              /* already closed */
            }
            return lastEventId;
          }
        }
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {
        /* already released */
      }
    }

    return lastEventId;
  };

  // Parse one SSE frame: collect the `id:` field and (possibly multi-line)
  // `data:` field. Comments (`:`) and other fields are ignored.
  private parseSseFrame = (
    raw: string
  ): { id: string | null; data: string | null } => {
    let id: string | null = null;
    const dataLines: string[] = [];

    for (const line of raw.split("\n")) {
      if (line.startsWith("id:")) {
        id = line.slice(3).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).replace(/^ /, ""));
      }
    }

    return { id, data: dataLines.length ? dataLines.join("\n") : null };
  };

  // The 9-event contract v2 dispatcher. `type` is the discriminator carried
  // inside the JSON; payload fields are siblings of `type`.
  private handleV2Event = (data: any, ctx: V2StreamContext) => {
    switch (data?.type) {
      case "run.started":
        if (data.thread_id) {
          // ctx.threadId is what the completion handler caches: on a first
          // turn it was null, and a remount then opened a second thread.
          ctx.threadId = data.thread_id;
          ctx.setThreadId(data.thread_id);
          chatCache.setThreadId(ctx.assistant.id, data.thread_id);
        }
        this.rememberWidgetToken(ctx.assistant.id, data);
        ctx.setIsMessageLoading(true);
        break;

      case "reasoning.started":
        // Model is thinking before the first token — keep the spinner up.
        ctx.setIsMessageLoading(true);
        break;

      case "message.delta":
        ctx.setIsMessageLoading(false);
        ctx.botResponse.messageReply += data.delta ?? "";
        this.upsertBotMessage(ctx);
        break;

      case "tool.started":
        // `tool` is the raw dengine tool name and the key TypingIndicator looks
        // up in tools_actions / tool_status_sequences, so it has to win. A
        // server-sent `title` would otherwise miss every localization entry and
        // silently fall the whole indicator back to the generic copy.
        ctx.setToolName(data.tool ?? data.title ?? null);
        break;

      case "tool.finished":
        ctx.setToolName(null);
        break;

      case "ping":
        // Heartbeat — no-op.
        break;

      case "message.completed":
        this.handleV2Completed(data, ctx);
        break;

      case "run.error":
        this.handleV2Error(data, ctx);
        break;

      case "run.finished":
        // Terminal frame on success (message.completed already applied).
        ctx.setIsMessageLoading(false);
        ctx.setIsMessageCompleted(true);
        ctx.setToolName(null);
        ctx.done = true;
        break;

      default:
        break;
    }
  };

  private upsertBotMessage = (ctx: V2StreamContext) => {
    ctx.setMessages((prevMessages: any) => {
      const updatedMessages = [...prevMessages];
      const lastIndex = updatedMessages.length - 1;
      const lastMessage = updatedMessages[lastIndex];

      if (lastMessage && lastMessage.type === "bot") {
        updatedMessages[lastIndex] = {
          ...lastMessage,
          content: ctx.botResponse.messageReply,
        };
      } else {
        updatedMessages.push({
          id: ctx.messageId,
          type: "bot",
          content: ctx.botResponse.messageReply,
          metadata: ctx.botResponse.metadata,
          time: Utility.getTime(),
        });
      }
      return updatedMessages;
    });
  };

  private handleV2Completed = (data: any, ctx: V2StreamContext) => {
    ctx.setIsMessageCompleted(true);

    // v2 templates arrive as structured objects — no JSON.parse.
    // Quote pills are pulled out FIRST and never travel with the rest: they are
    // in-bubble chrome, and left in the array they would fall through
    // isTextTemplate into openSlider, which would put an anchor card on the
    // reply for something that has no card.
    const { pills: quotePills, rest } = this.splitQuotePills(data.templates);
    const responseTemplates = this.normalizeTemplates(rest);
    const citations = Array.isArray(data.citations) ? data.citations : null;
    const isText =
      responseTemplates && this.isTextTemplate(responseTemplates);

    Analytics.track("reply_received", {
      has_templates: !!responseTemplates,
      template_types: responseTemplates
        ? Array.from(new Set(responseTemplates.map((t: any) => t?.type)))
        : [],
    });

    // HM hotel/offer cards render inline in the message and never reach
    // openSlider — log their exposure here or it's lost.
    if (responseTemplates && isText) {
      Analytics.track("templates_shown", {
        template_types: Array.from(
          new Set(responseTemplates.map((t: any) => t?.type))
        ),
        inline: true,
      });
    }

    if (responseTemplates && !isText) {
      useSliderState
        .getState()
        .openSlider(ctx.threadId, ctx.assistant.id, responseTemplates, ctx.isMobile);
    }

    const metadata = data.metadata ?? null;
    // sensitive_alerts is not a top-level v2 field; the assembler folds it into
    // metadata. Read both shapes so we never drop the alert.
    const sensitiveAlerts =
      data.metadata?.sensitive_alerts ?? data.sensitive_alerts ?? null;
    const finalText = data.text ?? ctx.botResponse.messageReply;
    ctx.botResponse.messageReply = finalText;

    ctx.setMessages((prevMessages: any) => {
      const updatedMessages = [...prevMessages];
      const lastIndex = updatedMessages.length - 1;
      const lastMessage = updatedMessages[lastIndex];

      if (lastMessage && lastMessage.type === "bot") {
        if (quotePills.length) {
          lastMessage.quotePills = quotePills;
        }
        if (responseTemplates && isText) {
          lastMessage.templates = responseTemplates;
        }
        if (responseTemplates && !isText) {
          // Anchor card data: the reply keeps its slider templates so the chat
          // can reopen them later (also survives reloads via the cache write).
          lastMessage.sliderTemplates = responseTemplates;
        }

        updatedMessages[lastIndex] = {
          ...lastMessage,
          content: finalText,
          metadata,
          sensitive_alerts: sensitiveAlerts,
          citations,
        };

        chatCache.addMessage(
          updatedMessages[lastIndex],
          ctx.threadId,
          ctx.assistant.id
        );
      }

      return updatedMessages;
    });

    ctx.setToolName(null);

    // The host site's half of the conversation. `message` told it what the
    // guest said; this is the only place that can tell it what came back.
    if (!ctx.replyEmitted) {
      ctx.replyEmitted = true;
      HostApi.emit("reply", {
        text: finalText,
        threadId: ctx.threadId,
        hasTemplates: !!responseTemplates,
      });
    }
  };

  private handleV2Error = (data: any, ctx: V2StreamContext) => {
    ctx.setIsMessageCompleted(true);
    ctx.botResponse.messageReply += data?.message ?? data?.code ?? "";

    ctx.setMessages((prevMessages: any) => {
      const updatedMessages = [...prevMessages];
      updatedMessages.push({
        id: ctx.messageId,
        type: "bot",
        content: ctx.botResponse.messageReply,
        metadata: ctx.botResponse.metadata,
        time: Utility.getTime(),
      });
      return updatedMessages;
    });

    ctx.setIsMessageLoading(false);
    ctx.setToolName(null);
    ctx.done = true;
  };

  // Transport-level failure (couldn't open/read the stream). Unlike a server
  // `run.error` frame there's no message to render, so mirror the legacy
  // EventSource.onerror behaviour: stop the spinner and end the turn quietly.
  private emitStreamError = (ctx: V2StreamContext, err: any) => {
    console.error("v2 stream failed:", err);
    ctx.setIsMessageCompleted(true);
    ctx.setIsMessageLoading(false);
    ctx.setToolName(null);
    ctx.done = true;
  };

  // Splits the quote-pill template out of the turn's templates. Returns the
  // pills themselves (already unwrapped from the template envelope, so the
  // renderer never sees `type`) and everything else untouched.
  //
  // Tolerant of a single object, of several pill templates in one turn, and of a
  // template whose `pills` is missing or malformed: a bad payload renders no
  // pills, which is the behaviour that predates them. It must never throw here —
  // this runs inside the frame that also carries the reply text.
  private splitQuotePills = (templates: any): { pills: any[]; rest: any[] } => {
    if (!templates) return { pills: [], rest: [] };
    const arr = Array.isArray(templates) ? templates : [templates];

    const pills: any[] = [];
    const rest: any[] = [];

    for (const template of arr) {
      if (template?.type !== "quote_pills") {
        rest.push(template);
        continue;
      }
      if (!Array.isArray(template.pills)) continue;
      for (const pill of template.pills) {
        if (typeof pill?.party === "number" && pill.party > 0) pills.push(pill);
      }
    }

    return { pills, rest };
  };

  // v2 templates are already structured objects/arrays — no string parsing.
  private normalizeTemplates = (templates: any): any[] | null => {
    if (!templates) return null;
    const arr = Array.isArray(templates) ? templates : [templates];
    return arr.length ? arr : null;
  };

  private isTextTemplate = (templates: any[]) => {
    return ["hm_hotel", "hm_offer"].includes(templates[0]?.type);
  };

  // dengine returns `widget_token` wherever it returns `thread_id`. All three
  // shapes carry it on the payload root: the non-stream JSON body, the v1
  // stream's final payload, and the v2 run.started frame. The retired v1
  // envelope nested it under `event`, so read both. A missing token is null,
  // never a throw.
  private readWidgetToken = (payload: any): string | null =>
    payload?.widget_token ?? payload?.event?.widget_token ?? null;

  // Persist the token next to the thread id as soon as the engine hands it
  // over, so every later backend call can carry it.
  private rememberWidgetToken = (assistantId: any, payload: any) => {
    const token = this.readWidgetToken(payload);
    if (token) chatCache.setWidgetToken(assistantId, token);
  };
}
